import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Star, Key } from 'lucide-react';

const PrestatairesList = () => {
  const [prestataires, setPrestataires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [selectedPrestataire, setSelectedPrestataire] = useState(null);
  const [accountPassword, setAccountPassword] = useState('');
  const [accountRole, setAccountRole] = useState('prestataire');
  const [formData, setFormData] = useState({
    nom: '', prenom: '', email: '', telephone: '', role: 'videaste', is_jeremy: false, password: '', account_role: 'prestataire'
  });

  useEffect(() => {
    loadPrestataires();
  }, []);

  const loadPrestataires = async () => {
    try {
      const res = await axios.get(`${API}/prestataires`, { withCredentials: true });
      setPrestataires(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/prestataires/${editingItem.id}`, formData, { withCredentials: true });
        toast.success('Prestataire mis à jour');
      } else {
        const response = await axios.post(`${API}/prestataires`, formData, { withCredentials: true });
        toast.success('Prestataire créé');
        
        // If password provided, create account automatically
        if (formData.password && formData.password.length >= 8) {
          try {
            await axios.post(
              `${API}/prestataires/${response.data.id}/create-account`,
              { password: formData.password, role: formData.account_role },
              { withCredentials: true }
            );
            toast.success(`Compte ${formData.account_role === 'admin' ? 'administrateur' : 'prestataire'} créé avec succès !`);
          } catch (err) {
            toast.warning('Prestataire créé mais erreur lors de la création du compte');
          }
        }
      }
      loadPrestataires();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nom: item.nom,
      prenom: item.prenom,
      email: item.email,
      telephone: item.telephone,
      role: item.role,
      is_jeremy: item.is_jeremy,
      password: ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce prestataire ?')) return;
    try {
      await axios.delete(`${API}/prestataires/${id}`, { withCredentials: true });
      toast.success('Prestataire supprimé');
      loadPrestataires();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({ nom: '', prenom: '', email: '', telephone: '', role: 'videaste', is_jeremy: false, password: '', account_role: 'prestataire' });
    setEditingItem(null);
  };

  const handleCreateAccount = async () => {
    if (!accountPassword || accountPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    try {
      await axios.post(`${API}/prestataires/${selectedPrestataire.id}/create-account`, 
        { password: accountPassword, role: accountRole }, 
        { withCredentials: true }
      );
      toast.success(`Compte ${accountRole === 'admin' ? 'administrateur' : 'prestataire'} créé avec succès !`);
      setAccountDialogOpen(false);
      setAccountPassword('');
      setAccountRole('prestataire');
      setSelectedPrestataire(null);
      loadPrestataires();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    }
  };

  const getRoleBadge = (role) => {
    const colors = { videaste: 'bg-blue-100 text-blue-700', photographe: 'bg-purple-100 text-purple-700', magnets: 'bg-amber-100 text-amber-700', drone: 'bg-emerald-100 text-emerald-700' };
    return <Badge className={colors[role] || 'bg-stone-100'}>{role}</Badge>;
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Prestataires</h1>
          <p className="text-stone-600">Gérez votre équipe</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-600 hover:bg-amber-700" data-testid="btn-add-prestataire">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau prestataire
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Modifier' : 'Nouveau prestataire'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom *</Label>
                  <Input value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} required />
                </div>
                <div>
                  <Label>Prénom *</Label>
                  <Input value={formData.prenom} onChange={(e) => setFormData({ ...formData, prenom: e.target.value })} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                </div>
                <div>
                  <Label>Téléphone *</Label>
                  <Input value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} required />
                </div>
              </div>
              <div>
                <Label>Rôle</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="videaste">Vidéaste</SelectItem>
                    <SelectItem value="photographe">Photographe</SelectItem>
                    <SelectItem value="magnets">Magnets</SelectItem>
                    <SelectItem value="drone">Drone</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!editingItem && (
                <>
                  <div>
                    <Label>Mot de passe (optionnel)</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Minimum 8 caractères pour créer un compte automatiquement"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      Si fourni (8+ caractères), un compte de connexion sera créé automatiquement
                    </p>
                  </div>
                  {formData.password && formData.password.length >= 8 && (
                    <div>
                      <Label>Rôle du compte</Label>
                      <Select
                        value={formData.account_role}
                        onValueChange={(val) => setFormData({ ...formData, account_role: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="prestataire">
                            <div className="flex items-center gap-2">
                              <span>👤</span>
                              <span>Prestataire - Accès limité (feuilles de route)</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="admin">
                            <div className="flex items-center gap-2">
                              <span>👑</span>
                              <span>Administrateur - Accès complet au backoffice</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_jeremy"
                  checked={formData.is_jeremy}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_jeremy: checked })}
                />
                <Label htmlFor="is_jeremy">C&apos;est moi (Jeremy) - Coût = 0€</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700">{editingItem ? 'Modifier' : 'Créer'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8">Chargement...</TableCell></TableRow>
              ) : prestataires.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-stone-500">Aucun prestataire</TableCell></TableRow>
              ) : (
                prestataires.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="font-medium">{p.prenom} {p.nom}</div>
                        {p.is_jeremy && <Star className="h-4 w-4 text-amber-500 fill-amber-500" />}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{p.email}</div>
                      <div className="text-sm text-stone-500">{p.telephone}</div>
                    </TableCell>
                    <TableCell>{getRoleBadge(p.role)}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          setSelectedPrestataire(p);
                          setAccountDialogOpen(true);
                        }}
                        title="Créer un compte de connexion"
                      >
                        <Key className="h-4 w-4 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(p)}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog pour créer un compte prestataire */}
      <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un compte de connexion</DialogTitle>
          </DialogHeader>
          {selectedPrestataire && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm text-blue-800">
                  <strong>{selectedPrestataire.prenom} {selectedPrestataire.nom}</strong> pourra se connecter pour consulter ses feuilles de route.
                </p>
              </div>
              <div>
                <Label>Email de connexion</Label>
                <Input value={selectedPrestataire.email} disabled className="bg-stone-100" />
              </div>
              <div>
                <Label>Mot de passe *</Label>
                <Input
                  type="password"
                  value={accountPassword}
                  onChange={(e) => setAccountPassword(e.target.value)}
                  placeholder="Minimum 8 caractères"
                  minLength={8}
                />
              </div>
              <div>
                <Label>Rôle du compte</Label>
                <Select value={accountRole} onValueChange={setAccountRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prestataire">
                      <div className="flex items-center gap-2">
                        <span>👤</span>
                        <span>Prestataire - Accès limité aux feuilles de route</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <span>👑</span>
                        <span>Administrateur - Accès complet au backoffice</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <p className="text-xs text-amber-800">
                  💡 {accountRole === 'admin' ? 'Cet utilisateur aura accès complet au backoffice comme vous.' : 'Le prestataire verra uniquement ses feuilles de route assignées.'}
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => {
                  setAccountDialogOpen(false);
                  setAccountPassword('');
                  setSelectedPrestataire(null);
                }}>
                  Annuler
                </Button>
                <Button onClick={handleCreateAccount} className="bg-blue-600 hover:bg-blue-700">
                  Créer le compte
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrestatairesList;
